import { useCallback, useEffect, useRef, useState } from "react";
import { Camera, Upload } from "lucide-react";

import { AgentPhoto } from "../workerLine/AgentViews.jsx";
import { getAgent, uploadAgentPhoto } from "../workerLine/workerLineApi.js";
import { describeWriteError } from "../workerLine/workerLineShared.js";

/**
 * P165: an Agent's picture, attachable from the Super Agent drill-down.
 *
 * Owner: "agent ให้แนบรูปได้ด้วยครับ".
 *
 * The picture itself is not new - P161 built the upload route, the storage
 * profile and the field on the Agents list screen's own create/edit forms. What
 * was missing is THIS page: /backoffice/super-agents/:id/plcs/:id/agents/:id is
 * built from the worker-intake hierarchy TREE, which carries names and counts
 * and no picture at all, so an operator who reached an Agent by drilling down
 * from a Super Agent had no photo and no way to add one without navigating to a
 * different screen and finding the same Agent again.
 *
 * It reads the Agent's real record itself for that reason: the tree this page
 * renders from has no `photo` field to pass down, and inventing one from the
 * tree would be guessing at whether a picture exists.
 */
export default function AgentPhotoCard({ agentId, displayName }) {
  const inputRef = useRef(null);
  const [state, setState] = useState({ status: "loading", hasPhoto: false, canWrite: false });
  const [pending, setPending] = useState(false);
  const [error, setError] = useState(null);
  // Bumped after a successful upload so AgentPhoto refetches: the URL it reads
  // is the same either way, so without this the old picture stays on screen.
  const [photoToken, setPhotoToken] = useState(0);

  const load = useCallback(() => {
    if (!agentId) {
      return undefined;
    }
    let cancelled = false;
    setState((previous) => ({ ...previous, status: "loading" }));
    getAgent(agentId)
      .then((detail) => {
        if (!cancelled) {
          setState({ status: "ready", hasPhoto: Boolean(detail?.photo), canWrite: true });
        }
      })
      .catch(() => {
        // A reader who cannot open the Agent's own record gets the placeholder
        // and no upload control, rather than a control that would 403.
        if (!cancelled) {
          setState({ status: "error", hasPhoto: false, canWrite: false });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [agentId]);

  useEffect(() => load(), [load]);

  function attach(file) {
    if (!file) {
      return;
    }
    setPending(true);
    setError(null);
    uploadAgentPhoto(agentId, file)
      .then(() => {
        setState((previous) => ({ ...previous, hasPhoto: true }));
        setPhotoToken((token) => token + 1);
      })
      .catch((caught) => {
        // The shared mapper turns every 400 into "กรุณาตรวจสอบข้อมูลที่กรอกอีก
        // ครั้ง", which is right for a form with several fields and useless for
        // one file: the server's own 400 says exactly what was wrong with it
        // (wrong type, too large), so that message is preferred when there is
        // one. Left alone for every other status, and for a 400 with no body.
        const fromServer = caught?.status === 400 ? caught?.body?.message : null;
        setError(fromServer || describeWriteError(caught));
      })
      .finally(() => setPending(false));
  }

  return (
    <div className="wx-sao-agentphoto">
      <AgentPhoto
        // The token is part of the key, not a prop: remounting is what makes it
        // read the new bytes, and it keeps this component free of any knowledge
        // of how AgentPhoto caches.
        key={photoToken}
        agentId={agentId}
        hasPhoto={state.hasPhoto}
        displayName={displayName}
        size="lg"
      />

      {state.canWrite && (
        <div className="wx-sao-agentphoto-controls">
          <button
            type="button"
            className="wx-button wx-button--ghost"
            disabled={pending}
            onClick={() => inputRef.current?.click()}
          >
            {pending ? <Upload size={14} aria-hidden="true" /> : <Camera size={14} aria-hidden="true" />}
            {pending ? "กำลังอัปโหลด..." : state.hasPhoto ? "เปลี่ยนรูป" : "แนบรูป"}
          </button>
          <input
            ref={inputRef}
            type="file"
            className="wx-visually-hidden"
            aria-label={`แนบรูปของ ${displayName || "Agent"}`}
            // The same three types the server's own allow-list accepts. This
            // only filters the file dialog; the real check is the server's
            // magic-number test, which nothing here can replace.
            accept="image/jpeg,image/png,image/webp"
            disabled={pending}
            onChange={(event) => {
              const file = event.target.files?.[0];
              // Cleared so picking the same file twice still fires a change.
              if (inputRef.current) {
                inputRef.current.value = "";
              }
              attach(file);
            }}
          />
        </div>
      )}

      {error && (
        <p className="wx-sao-agentphoto-error" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
